import { signal } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import { applyTheme, getActiveTheme, type Theme } from "@app/ui/lib/theme";
import {
  ArrowLeft,
  Gamepad2,
  Map as MapIcon,
  Maximize2,
  Minimize2,
  RefreshCw,
  Zap
} from "lucide-react";

const GAME_SOURCES = new Set([
  "trail-next-frame",
  "snake-retro-frame",
  "empilhar-frame",
  "breakout-frame",
  "flappy-dash-frame",
  "crossy-frog-frame",
  "pong-arcade-frame",
  "retro-highway-frame",
  "block-puzzle-frame",
  "space-invaders-frame",
  "artillery-strike-frame",
  "columns-match-frame",
  "lunar-lander-frame",
  "mini-maze-frame",
  "piano-tiles-frame"
]);

interface Jogo {
  id: string;
  nome: string;
  categoria: string;
  descricao: string;
  url: string;
  icon: typeof Gamepad2;
  iconColor: string;
  iconBg: string;
  bridge?: "postmessage" | "none";
  // Overrides vindos da config do admin (manager-games-config). Quando
  // setados, sobrepõem nome/desc/categoria/thumbUrl/isNew. thumbUrl substitui
  // a cena custom (renderPreviewScene) pelo asset configurado.
  thumbUrl?: string | null;
  isNew?: boolean;
  // Mapeia ID do catálogo central pro ID local. O catálogo do server usa
  // "trilha"; o local usa "trail". Sem essa ponte os overrides não casam.
  catalogId?: string;
}

interface GameStateSnapshot {
  currentLevelIndex?: number;
  currentLevelName?: string;
  totalLevels?: number;
  completedLevels?: number;
  score?: number;
  bestScore?: number;
  speedMultiplier?: number;
  running?: boolean;
  theme?: Theme;
  vibrationEnabled?: boolean;
  hasSavedProgress?: boolean;
}

interface GameMessage {
  source: string;
  type: string;
  gameId?: string;
  version?: string;
  payload?: {
    theme?: Theme;
    hostApp?: string;
    reason?: string;
    state?: GameStateSnapshot;
    capabilities?: string[];
  };
}

const HOST_SOURCE = "student-new-game-host";
const JOGOS: Jogo[] = [
  {
    id: "trail",
    catalogId: "trilha",
    nome: "Trilha",
    categoria: "Puzzle",
    descricao:
      "Mova-se pelo tabuleiro respondendo questões. Ganha quem chegar primeiro ao fim da trilha.",
    url: "/games/trail-next/index.html",
    icon: MapIcon,
    iconColor: "text-emerald-600 dark:text-emerald-400",
    iconBg: "bg-emerald-500/10 ring-emerald-500/20",
    bridge: "postmessage"
  },
  {
    id: "snake-retro",
    nome: "Snake Retro",
    categoria: "Arcade",
    descricao:
      "Colete pontos, alongue a cobra e sobreviva em um arcade classico de ritmo crescente.",
    url: "/games/snake-retro/index.html",
    icon: Gamepad2,
    iconColor: "text-cyan-600 dark:text-cyan-400",
    iconBg: "bg-cyan-500/10 ring-cyan-500/20",
    bridge: "postmessage"
  },
  {
    id: "empilhar",
    nome: "Empilhar",
    categoria: "Puzzle",
    descricao:
      "Alinhe blocos em movimento, suba andar por andar e busque o maximo com precisao.",
    url: "/games/empilhar/index.html",
    icon: MapIcon,
    iconColor: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-amber-500/10 ring-amber-500/20",
    bridge: "postmessage"
  },
  {
    id: "breakout",
    nome: "Quebra-Blocos",
    categoria: "Arcade",
    descricao:
      "Rebata a bola, limpe a parede de blocos e sustente a rodada com controle fino.",
    url: "/games/breakout/index.html",
    icon: Zap,
    iconColor: "text-pink-600 dark:text-pink-400",
    iconBg: "bg-pink-500/10 ring-pink-500/20",
    bridge: "postmessage"
  },
  {
    id: "flappy-dash",
    nome: "Flappy Dash",
    categoria: "Arcade",
    descricao:
      "Atravesse as aberturas entre canos em um arcade curto de timing e impulso.",
    url: "/games/flappy-dash/index.html",
    icon: Gamepad2,
    iconColor: "text-sky-600 dark:text-sky-400",
    iconBg: "bg-sky-500/10 ring-sky-500/20",
    bridge: "postmessage"
  },
  {
    id: "crossy-frog",
    nome: "Crossy Frog",
    categoria: "Arcade",
    descricao:
      "Atravesse faixas com trafego crescente e suba nivel por nivel sem ser atingido.",
    url: "/games/crossy-frog/index.html",
    icon: MapIcon,
    iconColor: "text-lime-600 dark:text-lime-400",
    iconBg: "bg-lime-500/10 ring-lime-500/20",
    bridge: "postmessage"
  },
  {
    id: "pong-arcade",
    nome: "Pong Arcade",
    categoria: "Arcade",
    descricao:
      "Enfrente a CPU em partidas rapidas de rebatida e controle de angulo.",
    url: "/games/pong-arcade/index.html",
    icon: Gamepad2,
    iconColor: "text-emerald-600 dark:text-emerald-400",
    iconBg: "bg-emerald-500/10 ring-emerald-500/20",
    bridge: "postmessage"
  },
  {
    id: "retro-highway",
    nome: "Retro Highway",
    categoria: "Arcade",
    descricao:
      "Desvie do trafego, mantenha o carro na pista e empilhe distancia em ritmo arcade.",
    url: "/games/retro-highway/index.html",
    icon: Zap,
    iconColor: "text-red-600 dark:text-red-400",
    iconBg: "bg-red-500/10 ring-red-500/20",
    bridge: "postmessage"
  },
  {
    id: "block-puzzle",
    nome: "Block Puzzle",
    categoria: "Puzzle",
    descricao:
      "Encaixe pecas, limpe linhas completas e segure a pilha o maximo possivel.",
    url: "/games/block-puzzle/index.html",
    icon: MapIcon,
    iconColor: "text-blue-600 dark:text-blue-400",
    iconBg: "bg-blue-500/10 ring-blue-500/20",
    bridge: "postmessage"
  },
  {
    id: "space-invaders",
    nome: "Space Invaders",
    categoria: "Arcade",
    descricao:
      "Mova a nave, derrube a formacao alienigena e segure a linha final em ritmo arcade.",
    url: "/games/space-invaders/index.html",
    icon: Zap,
    iconColor: "text-sky-600 dark:text-sky-400",
    iconBg: "bg-sky-500/10 ring-sky-500/20",
    bridge: "postmessage"
  },
  {
    id: "artillery-strike",
    nome: "Artillery Strike",
    categoria: "Estrategia",
    descricao:
      "Ajuste angulo e forca para derrubar o alvo em um arcade curto de leitura balistica.",
    url: "/games/artillery-strike/index.html",
    icon: Zap,
    iconColor: "text-rose-600 dark:text-rose-400",
    iconBg: "bg-rose-500/10 ring-rose-500/20",
    bridge: "postmessage"
  },
  {
    id: "columns-match",
    nome: "Columns Match",
    categoria: "Puzzle",
    descricao:
      "Empilhe trios coloridos, forme linhas de 3 e busque combos com boa organizacao.",
    url: "/games/columns-match/index.html",
    icon: MapIcon,
    iconColor: "text-pink-600 dark:text-pink-400",
    iconBg: "bg-pink-500/10 ring-pink-500/20",
    bridge: "postmessage"
  },
  {
    id: "lunar-lander",
    nome: "Lunar Lander",
    categoria: "Arcade",
    descricao:
      "Dose o propulsor, controle a descida e toque a base com velocidade segura.",
    url: "/games/lunar-lander/index.html",
    icon: Zap,
    iconColor: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-amber-500/10 ring-amber-500/20",
    bridge: "postmessage"
  },
  {
    id: "mini-maze",
    nome: "Mini Maze",
    categoria: "Arcade",
    descricao:
      "Limpe o labirinto, colete todos os pontos e desvie do fantasma perseguidor.",
    url: "/games/mini-maze/index.html",
    icon: Gamepad2,
    iconColor: "text-yellow-600 dark:text-yellow-400",
    iconBg: "bg-yellow-500/10 ring-yellow-500/20",
    bridge: "postmessage"
  },
  {
    id: "piano-tiles",
    nome: "Piano Tiles",
    categoria: "Ritmo",
    descricao:
      "Toque so nas notas pretas e acompanhe o ritmo crescente sem errar a faixa.",
    url: "/games/piano-tiles/index.html",
    icon: Gamepad2,
    iconColor: "text-violet-600 dark:text-violet-400",
    iconBg: "bg-violet-500/10 ring-violet-500/20",
    bridge: "postmessage"
  }
];

const activeGameId = signal<string | null>(null);

// Overrides do admin via /api/student/games. Indexado por catalogId
// (mesmo do server, ex.: "trilha"). Quando vazio, JOGOS é usado puro.
interface GameOverride {
  id: string;
  name: string;
  desc: string;
  tag: string;
  isNew: boolean;
  thumbUrl: string | null;
}
const gameOverrides = signal<Record<string, GameOverride>>({});
const gameAreaEnabled = signal<boolean>(true);
let overridesFetched = false;

async function fetchGameOverrides() {
  if (overridesFetched) return;
  overridesFetched = true;
  try {
    const res = await fetch("/api/student/games", { credentials: "include" });
    if (!res.ok) return;
    const data = (await res.json()) as {
      areaEnabled: boolean;
      games: GameOverride[];
    };
    gameAreaEnabled.value = !!data.areaEnabled;
    const map: Record<string, GameOverride> = {};
    for (const g of data.games) map[g.id] = g;
    gameOverrides.value = map;
  } catch {
    /* silencia — fallback pra catálogo local */
  }
}

function applyOverride(jogo: Jogo): Jogo {
  const key = jogo.catalogId ?? jogo.id;
  const o = gameOverrides.value[key];
  if (!o) return jogo;
  return {
    ...jogo,
    nome: o.name || jogo.nome,
    descricao: o.desc || jogo.descricao,
    categoria: o.tag || jogo.categoria,
    thumbUrl: o.thumbUrl ?? null,
    isNew: o.isNew
  };
}

function syncJogosBreadcrumb(label?: string | null) {
  window.dispatchEvent(
    new CustomEvent("student:jogos-breadcrumb", {
      detail: { label: label ?? null }
    })
  );
}

function isThemeValue(value: unknown): value is Theme {
  return value === "light" || value === "dark";
}

function isGameMessage(value: unknown): value is GameMessage {
  return Boolean(
    value &&
      typeof value === "object" &&
      "source" in value &&
      "type" in value
  );
}

function buildEmbeddedGameUrl(url: string, reloadSeed: number) {
  const next = new URL(url, window.location.origin);
  next.searchParams.set("embedded", "1");
  next.searchParams.set("hostApp", "student-new");
  next.searchParams.set("reload", String(reloadSeed));
  return `${next.pathname}${next.search}${next.hash}`;
}

function postToGame(
  frame: HTMLIFrameElement | null,
  type: string,
  payload: Record<string, unknown> = {}
) {
  frame?.contentWindow?.postMessage(
    {
      source: HOST_SOURCE,
      type,
      payload: {
        hostApp: "student-new",
        ...payload
      }
    },
    window.location.origin
  );
}

export function JogosView() {
  const activeId = activeGameId.value;
  useEffect(() => {
    void fetchGameOverrides();
  }, []);
  useEffect(() => {
    const jogo = activeId ? JOGOS.find((item) => item.id === activeId) : null;
    syncJogosBreadcrumb(jogo?.nome ?? null);
    return () => syncJogosBreadcrumb(null);
  }, [activeId]);

  if (activeId) {
    const jogo = JOGOS.find((j) => j.id === activeId);
    if (jogo) return <JogoEmbedded jogo={applyOverride(jogo)} />;
    activeGameId.value = null;
  }
  return <JogosLista />;
}

function JogosLista() {
  // Pega o map de overrides (signal subscription) e mantém apenas os jogos
  // habilitados. Quando o admin desabilita um jogo, ele some daqui — JOGOS
  // local não tem essa info, vem só do server.
  const overrides = gameOverrides.value;
  const haveOverrides = Object.keys(overrides).length > 0;
  const lista = JOGOS.filter((j) => {
    if (!haveOverrides) return true;
    const key = j.catalogId ?? j.id;
    return key in overrides;
  }).map(applyOverride);
  return (
    <div class="space-y-5">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Gamepad2 className="size-6 text-primary" />
          Jogos
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">
          Modos lúdicos para praticar com os bancos de questões.
        </p>
      </header>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {lista.map((jogo) => (
          <JogoCard key={jogo.id} jogo={jogo} />
        ))}
      </div>
    </div>
  );
}

function JogoCard({ jogo }: { jogo: Jogo }) {
  return (
    <button
      type="button"
      onClick={() => (activeGameId.value = jogo.id)}
      class="group flex flex-col items-start gap-4 rounded-md border border-border bg-card/70 px-5 py-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md hover:shadow-primary/10"
    >
      <div class="flex w-full items-center gap-3">
        <JogoPreviewThumb jogo={jogo} />
        <div class="min-w-0 flex-1">
          <h2 class="flex items-center gap-1.5 truncate text-base font-bold tracking-tight text-foreground">
            <span class="truncate">{jogo.nome}</span>
            {jogo.isNew && (
              <span class="inline-flex shrink-0 items-center rounded-md bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                Novo
              </span>
            )}
          </h2>
        </div>
        <span
          class={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${jogo.iconBg} ${jogo.iconColor}`}
        >
          {jogo.categoria}
        </span>
      </div>

      <div class="space-y-1">
        <p class="text-xs leading-relaxed text-muted-foreground">
          {jogo.descricao}
        </p>
      </div>
      <span class="mt-1 text-xs font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
        Abrir jogo →
      </span>
    </button>
  );
}

function JogoPreviewThumb({ jogo }: { jogo: Jogo }) {
  // Quando o admin configurou thumbUrl, prioriza o asset.
  // Em caso de erro de carregamento, cai pro renderPreviewScene custom.
  const [imgErrored, setImgErrored] = useState(false);
  const showImg = !!jogo.thumbUrl && !imgErrored;
  return (
    <div class="relative size-11 shrink-0 overflow-hidden rounded-md ring-1 ring-border bg-muted/40">
      <div class="relative h-full w-full">
        {showImg ? (
          <img
            src={jogo.thumbUrl!}
            alt={jogo.nome}
            loading="lazy"
            onError={() => setImgErrored(true)}
            class="h-full w-full object-cover"
          />
        ) : (
          renderPreviewScene(jogo)
        )}
      </div>
    </div>
  );
}

function renderPreviewScene(jogo: Jogo) {
  const arena =
    "absolute inset-3 rounded-[10px] border border-white/15 shadow-[inset_0_0_0_2px_rgba(255,255,255,0.04)]";

  switch (jogo.id) {
    case "trail":
      return (
        <div class="absolute inset-0 bg-[radial-gradient(circle_at_top,#123524,transparent_55%),linear-gradient(180deg,#07130d,#0f1c16)]">
          <div class="absolute inset-1 rounded-[6px] border border-white/15" />
          <div class="absolute left-1/2 top-1/2 grid -translate-x-1/2 -translate-y-1/2 grid-cols-4 gap-0.5">
            {Array.from({ length: 12 }).map((_, index) => (
              <span
                key={index}
                class={`size-4 rounded-[4px] ${
                  index === 7 || index === 12
                    ? "bg-slate-600"
                    : index === 2 || index === 17
                      ? "bg-amber-200"
                      : "bg-cyan-100"
                }`}
              />
            ))}
          </div>
        </div>
      );
    case "retro-highway":
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#06070a,#121826)]">
          <div class="absolute inset-y-1 left-1 w-1.5 rounded-full bg-emerald-700/70" />
          <div class="absolute inset-y-1 right-1 w-1.5 rounded-full bg-emerald-700/70" />
          <div class="absolute inset-y-1 left-2.5 right-2.5 rounded-[6px] border border-white/15 bg-slate-900/80" />
          <div class="absolute inset-y-2 left-1/2 w-0.5 -translate-x-1/2 bg-[repeating-linear-gradient(to_bottom,rgba(255,255,255,0.65)_0_4px,transparent_4px_8px)]" />
          <div class="absolute bottom-1.5 left-1/2 h-3.5 w-2.5 -translate-x-1/2 rounded-sm bg-rose-500" />
          <div class="absolute left-[34%] top-[28%] h-3 w-2.5 rounded-sm bg-sky-500/80" />
          <div class="absolute right-[30%] top-[16%] h-3 w-2.5 rounded-sm bg-amber-400/85" />
        </div>
      );
    case "snake-retro":
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#04070d,#08101a)]">
          <div class="absolute inset-1 rounded-[6px] border border-white/15" />
          <div class="absolute inset-1.5 rounded-[5px] bg-black/30" />
          <div class="absolute left-[18%] top-[34%] flex gap-0.5">
            {Array.from({ length: 4 }).map((_, index) => (
              <span key={index} class="size-2 rounded-[2px] bg-emerald-300/80" />
            ))}
          </div>
          <div class="absolute right-[18%] top-[54%] size-2 rounded-full bg-rose-400" />
        </div>
      );
    case "empilhar":
    case "block-puzzle":
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#091220,#121c31)]">
          <div class="absolute inset-1 rounded-[6px] border border-white/15" />
          <div class="absolute inset-x-[24%] bottom-1.5 top-1.5 rounded-[5px] border border-white/15 bg-slate-950/55" />
          <div class="absolute inset-x-[28%] bottom-2 grid grid-cols-3 gap-0.5">
            {["bg-sky-400", "bg-violet-400", "bg-amber-400", "bg-emerald-400", "bg-rose-400", "bg-cyan-300", "bg-fuchsia-400", "bg-lime-300"].map((cls, index) => (
              <span key={index} class={`h-2 rounded-[2px] ${cls}`} />
            ))}
          </div>
          <div class="absolute left-[38%] top-[26%] grid grid-cols-2 gap-0.5">
            <span class="size-2 rounded-[2px] bg-blue-400" />
            <span class="size-2 rounded-[2px] bg-blue-400" />
            <span class="size-2 rounded-[2px] bg-blue-400" />
            <span class="size-2 rounded-[2px] bg-blue-400/20" />
          </div>
        </div>
      );
    case "breakout":
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#161a2b,#0b1020)]">
          <div class="absolute inset-1 rounded-[6px] border border-white/15" />
          <div class="absolute inset-x-1.5 top-1.5 grid grid-cols-4 gap-0.5">
            {["bg-rose-400", "bg-amber-400", "bg-emerald-400", "bg-sky-400", "bg-violet-400"].flatMap((cls) =>
              Array.from({ length: 4 }).map((_, index) => (
                <span key={`${cls}-${index}`} class={`h-1.5 rounded-[2px] ${cls}`} />
              ))
            )}
          </div>
          <div class="absolute bottom-1.5 left-1/2 h-1.5 w-5 -translate-x-1/2 rounded-full bg-pink-400" />
          <div class="absolute bottom-4 left-[58%] size-1.5 rounded-full bg-white" />
        </div>
      );
    case "flappy-dash":
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#38bdf8,#a5f3fc_70%,#e0f2fe)]">
          <div class="absolute inset-1 rounded-[6px] border border-white/20" />
          <div class="absolute left-[28%] top-0 h-[38%] w-2.5 rounded-b-md bg-emerald-600" />
          <div class="absolute left-[28%] bottom-0 h-[32%] w-2.5 rounded-t-md bg-emerald-600" />
          <div class="absolute right-[24%] top-0 h-[22%] w-2.5 rounded-b-md bg-emerald-600" />
          <div class="absolute right-[24%] bottom-0 h-[46%] w-2.5 rounded-t-md bg-emerald-600" />
          <div class="absolute left-[44%] top-[52%] size-2.5 rounded-full bg-amber-400" />
        </div>
      );
    case "crossy-frog":
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#0f172a,#1e293b)]">
          <div class="absolute inset-1 rounded-[6px] border border-white/15" />
          <div class="absolute inset-x-1 top-1 h-1.5 rounded bg-slate-700" />
          <div class="absolute inset-x-1 bottom-1 h-1.5 rounded bg-slate-700" />
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} class="absolute inset-x-1 h-2.5 bg-slate-800/80" style={{ top: `${30 + index * 16}%` }} />
          ))}
          <div class="absolute left-[18%] top-[36%] h-1.5 w-4 rounded bg-rose-400" />
          <div class="absolute right-[20%] top-[54%] h-1.5 w-5 rounded bg-amber-400" />
          <div class="absolute left-1/2 bottom-2 size-2.5 -translate-x-1/2 rounded-[3px] bg-lime-400" />
        </div>
      );
    case "pong-arcade":
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#03261d,#02150f)]">
          <div class="absolute inset-1 rounded-[6px] border border-white/15" />
          <div class="absolute inset-y-1 left-1/2 w-px -translate-x-1/2 bg-[repeating-linear-gradient(to_bottom,rgba(255,255,255,0.5)_0_4px,transparent_4px_8px)]" />
          <div class="absolute left-1/2 top-1.5 h-1.5 w-5 -translate-x-1/2 rounded-full bg-white" />
          <div class="absolute bottom-1.5 left-1/2 h-1.5 w-5 -translate-x-1/2 rounded-full bg-white" />
          <div class="absolute left-[58%] top-[52%] size-1.5 rounded-full bg-white" />
        </div>
      );
    case "space-invaders":
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#020205,#0d1020)]">
          <div class="absolute inset-1 rounded-[6px] border border-white/15" />
          <div class="absolute inset-x-1.5 top-1.5 grid grid-cols-4 gap-0.5">
            {Array.from({ length: 12 }).map((_, index) => (
              <span key={index} class="mx-auto h-1.5 w-3 rounded-[2px] bg-violet-400" />
            ))}
          </div>
          <div class="absolute bottom-1.5 left-1/2 h-2.5 w-4 -translate-x-1/2 rounded-t-md bg-sky-400" />
          <div class="absolute bottom-4.5 left-1/2 h-3 w-0.5 -translate-x-1/2 bg-amber-300" />
        </div>
      );
    case "artillery-strike":
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#1e1b4b,#311042)]">
          <div class="absolute inset-1 rounded-[6px] border border-white/15" />
          <div class="absolute bottom-1 inset-x-1 h-2.5 rounded bg-slate-800" />
          <div class="absolute left-[46%] bottom-3 h-4 w-2 rounded bg-slate-500" />
          <div class="absolute right-[18%] bottom-3 h-5 w-3 rounded bg-sky-400" />
          <div class="absolute left-[20%] bottom-2 h-2.5 w-4 rounded-full bg-rose-500" />
          <div class="absolute left-[28%] top-[34%] h-0.5 w-5 rotate-[-30deg] rounded bg-white/80" />
        </div>
      );
    case "columns-match":
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#12101b,#171226)]">
          <div class="absolute inset-1 rounded-[6px] border border-white/15" />
          <div class="absolute inset-x-[28%] top-1 bottom-1 rounded-[5px] border border-white/15 bg-black/25" />
          <div class="absolute inset-x-[31%] bottom-1.5 grid grid-cols-3 gap-0.5">
            {["bg-red-400","bg-blue-400","bg-green-400","bg-yellow-400","bg-red-400","bg-blue-400"].map((cls,index)=>(
              <span key={index} class={`h-2 rounded-[2px] ${cls}`} />
            ))}
          </div>
          <div class="absolute left-[42%] top-[24%] grid gap-0.5">
            <span class="size-2 rounded-[2px] bg-yellow-400" />
            <span class="size-2 rounded-[2px] bg-blue-400" />
            <span class="size-2 rounded-[2px] bg-red-400" />
          </div>
        </div>
      );
    case "lunar-lander":
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#050816,#0d1323)]">
          <div class="absolute inset-1 rounded-[6px] border border-white/15" />
          <div class="absolute inset-x-1 bottom-1 h-3 rounded bg-slate-700" />
          <div class="absolute left-[34%] bottom-1.5 h-1.5 w-8 rounded bg-amber-400" />
          <div class="absolute left-[54%] top-[38%] h-3 w-2.5 rounded bg-slate-200" />
          <div class="absolute left-[55%] top-[49%] h-2 w-1.5 rounded bg-red-400/80" />
        </div>
      );
    case "mini-maze":
      return (
        <div class="absolute inset-0 bg-black">
          <div class="absolute inset-1 rounded-[6px] border border-white/15" />
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} class="absolute bg-blue-800" style={{ left: `${10 + index * 18}%`, top: `${18 + (index % 2) * 20}%`, width: "18%", height: "8%" }} />
          ))}
          <div class="absolute left-[26%] top-[60%] size-2 rounded-full bg-yellow-400" />
          <div class="absolute left-[52%] top-[36%] size-2 rounded-full bg-yellow-400" />
          <div class="absolute right-[20%] top-[58%] h-3 w-3 rounded bg-red-400" />
          <div class="absolute left-[16%] top-[18%] size-3 rounded-full bg-yellow-300" />
        </div>
      );
    case "piano-tiles":
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#1e1e24,#27272f)]">
          <div class="absolute inset-1 rounded-[6px] border border-white/15" />
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} class="absolute top-1 bottom-1 w-px bg-white/15" style={{ left: `${25 * (index + 1)}%` }} />
          ))}
          <div class="absolute left-[4%] top-[18%] h-4 w-[20%] rounded bg-black" />
          <div class="absolute left-[52%] top-[42%] h-4 w-[20%] rounded bg-black" />
          <div class="absolute right-[4%] bottom-[16%] h-4 w-[20%] rounded bg-violet-400/35" />
        </div>
      );
    default:
      return (
        <div class="absolute inset-0 bg-[linear-gradient(180deg,#151a28,#0e1525)]">
          <div class={arena} />
        </div>
      );
  }
}

function JogoEmbedded({ jogo }: { jogo: Jogo }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isFs, setIsFs] = useState(false);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [gameReady, setGameReady] = useState(false);
  const [reloadSeed, setReloadSeed] = useState(0);
  const [gameState, setGameState] = useState<GameStateSnapshot | null>(null);
  const [wrapperHeight, setWrapperHeight] = useState(720);

  const usesBridge = jogo.bridge !== "none";

  function sendHostInit() {
    if (!usesBridge) return;
    postToGame(iframeRef.current, "host:init", {
      theme: getActiveTheme()
    });
    postToGame(iframeRef.current, "host:fullscreen-changed", {
      isFullscreen: document.fullscreenElement === wrapperRef.current
    });
  }

  function syncThemeToGame(theme: Theme) {
    if (!usesBridge) return;
    postToGame(iframeRef.current, "host:set-theme", { theme });
  }

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const measure = () => {
      const rect = wrapper.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const footer = wrapper.parentElement?.nextElementSibling as HTMLElement | null;
      const footerHeight =
        !isFs && footer ? Math.max(0, Math.ceil(footer.getBoundingClientRect().height)) : 0;
      const bottomReserve = isFs
        ? 12
        : footerHeight + (window.innerWidth >= 768 ? 20 : 12);
      // Mínimo bem baixo (240) pra que SEMPRE caiba na viewport — evita
      // criar scroll na página, que faz arrow keys e space deslocarem o
      // conteúdo enquanto o aluno joga. Em viewports apertados o jogo fica
      // baixinho mas ainda jogável; preferível a scrollbar parasita.
      const nextHeight = Math.max(
        240,
        Math.floor(viewportHeight - rect.top - bottomReserve)
      );
      setWrapperHeight((current) =>
        Math.abs(current - nextHeight) > 1 ? nextHeight : current
      );
    };

    measure();
    const raf1 = window.requestAnimationFrame(measure);
    const timeoutId = window.setTimeout(measure, 120);
    window.addEventListener("resize", measure);

    return () => {
      window.cancelAnimationFrame(raf1);
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", measure);
    };
  }, [isFs, jogo.id]);

  useEffect(() => {
    const onChange = () => {
      const nextIsFs = document.fullscreenElement === wrapperRef.current;
      setIsFs(nextIsFs);
      if (!usesBridge) return;
      postToGame(iframeRef.current, "host:fullscreen-changed", {
        isFullscreen: nextIsFs
      });
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  // Trava o scroll da página enquanto o jogo está montado + intercepta as
  // teclas de movimento (setas + espaço + page up/down + home/end) pra que
  // NUNCA disparem scroll da página por trás do iframe. Quando o foco está
  // dentro do iframe o browser entrega direto pro jogo; problema é quando o
  // foco está no document — aí o default action é scroll. preventDefault
  // mata essa parte sem impedir o jogo de receber a tecla via window do
  // próprio iframe.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const SCROLLING_KEYS = new Set([
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      " ",
      "Spacebar", // legacy Firefox
      "PageUp",
      "PageDown",
      "Home",
      "End"
    ]);
    function blockScrollKeys(e: KeyboardEvent) {
      if (!SCROLLING_KEYS.has(e.key)) return;
      const t = e.target as HTMLElement | null;
      // Se o aluno digitou num input/textarea/contenteditable, não bloqueia
      // (não tem como, é input legítimo).
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) {
        return;
      }
      e.preventDefault();
    }
    window.addEventListener("keydown", blockScrollKeys, { passive: false });
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", blockScrollKeys);
    };
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data === "close-game") {
        activeGameId.value = null;
        return;
      }
      if (!usesBridge) return;
      if (!isGameMessage(event.data) || !GAME_SOURCES.has(event.data.source)) {
        return;
      }

      const snapshot = event.data.payload?.state ?? null;
      if (snapshot) setGameState(snapshot);

      if (event.data.type.endsWith(":ready")) {
        setGameReady(true);
        sendHostInit();
        postToGame(iframeRef.current, "host:request-state");
        return;
      }

      if (event.data.type.endsWith(":close-requested")) {
        activeGameId.value = null;
        return;
      }

      if (event.data.type.endsWith(":theme-change-requested")) {
        if (isThemeValue(event.data.payload?.theme)) {
          applyTheme(event.data.payload.theme);
        }
      }
    };

    const onThemeChanged = (event: Event) => {
      const nextTheme = (event as CustomEvent<{ theme?: Theme }>).detail?.theme;
      if (isThemeValue(nextTheme)) syncThemeToGame(nextTheme);
    };

    window.addEventListener("message", onMessage);
    window.addEventListener("theme:changed", onThemeChanged as EventListener);
    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("theme:changed", onThemeChanged as EventListener);
    };
  }, []);

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
      return;
    }
    const el = wrapperRef.current;
    if (!el) return;
    el.requestFullscreen?.().catch((err) => console.warn("[fullscreen]", err));
  }

  function closeEmbeddedGame() {
    if (document.fullscreenElement === wrapperRef.current) {
      document.exitFullscreen().catch(() => {});
    }
    activeGameId.value = null;
  }

  function reloadGame() {
    setFrameLoaded(false);
    setGameReady(false);
    setGameState(null);
    setReloadSeed((value) => value + 1);
  }

  const frameSrc = buildEmbeddedGameUrl(jogo.url, reloadSeed);
  const statusText = !frameLoaded
    ? "Carregando shell do jogo..."
    : !gameReady
      ? "Conectando ao jogo..."
      : gameState?.currentLevelName
        ? `${gameState.currentLevelName}${typeof gameState.completedLevels === "number" ? ` · ${gameState.completedLevels} concluídas` : ""}`
        : typeof gameState?.score === "number"
          ? `Pontuação ${gameState.score}${typeof gameState.bestScore === "number" ? ` · melhor ${gameState.bestScore}` : ""}${typeof gameState.speedMultiplier === "number" ? ` · ${gameState.speedMultiplier.toFixed(1)}x` : ""}`
        : "Jogo conectado";

  return (
    <div
      ref={wrapperRef}
      class={`flex flex-col gap-3 overflow-hidden ${isFs ? "h-screen w-screen bg-background p-3" : ""}`}
      style={
        isFs
          ? undefined
          : { height: `${wrapperHeight}px`, minHeight: `${wrapperHeight}px` }
      }
    >
      <header class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/60 px-3 py-2">
        <button
          type="button"
          onClick={closeEmbeddedGame}
          title="Voltar para a lista de jogos"
          class="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-xs font-semibold hover:bg-accent"
        >
          <ArrowLeft width="1.1em" height="1.1em" />
          Voltar
        </button>
        <div class="min-w-0">
          <h2 class="text-sm font-bold">{jogo.nome}</h2>
          <p class="truncate text-xs text-muted-foreground">{statusText}</p>
        </div>
        <span
          class={`ml-auto rounded-full border px-2 py-1 text-[11px] font-semibold ${
            gameReady
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
              : "border-border bg-background text-muted-foreground"
          }`}
        >
          {gameReady ? (usesBridge ? "Integrado" : "Pronto") : "Conectando"}
        </span>
        <button
          type="button"
          onClick={reloadGame}
          title="Recarregar jogo"
          class="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-xs font-semibold hover:bg-accent"
        >
          <RefreshCw width="1.1em" height="1.1em" />
          <span class="hidden sm:inline">Recarregar</span>
        </button>
        <button
          type="button"
          onClick={toggleFullscreen}
          title={isFs ? "Sair da tela cheia" : "Tela cheia"}
          class="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-xs font-semibold hover:bg-accent"
        >
          {isFs ? (
            <Minimize2 width="1.1em" height="1.1em" />
          ) : (
            <Maximize2 width="1.1em" height="1.1em" />
          )}
          <span class="hidden sm:inline">
            {isFs ? "Sair da tela cheia" : "Tela cheia"}
          </span>
        </button>
      </header>
      <div
        class="relative min-h-0 flex-1 overflow-hidden"
      >
        {!gameReady ? (
          <div class="pointer-events-none absolute inset-0 z-10 flex items-center justify-center border border-border bg-background/75 backdrop-blur-[1px]">
            <div class="rounded-md border border-border bg-card px-4 py-3 text-center shadow-sm">
              <p class="text-sm font-semibold text-foreground">
                Preparando {jogo.nome}
              </p>
              <p class="mt-1 text-xs text-muted-foreground">{statusText}</p>
            </div>
          </div>
        ) : null}
        <iframe
          key={reloadSeed}
          ref={iframeRef}
          src={frameSrc}
          title={jogo.nome}
          onLoad={() => {
            setFrameLoaded(true);
            if (!usesBridge) {
              setGameReady(true);
              return;
            }
            sendHostInit();
            window.setTimeout(() => {
              sendHostInit();
              postToGame(iframeRef.current, "host:request-state");
            }, 140);
          }}
          class="block h-full w-full border border-border bg-background"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-pointer-lock"
          allow="fullscreen"
        />
      </div>
    </div>
  );
}


